import type { RealtimeChannel } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";

type SharedChannelEntry = {
  channel: RealtimeChannel;
  refs: number;
  listeners: Set<() => void>;
};

const sharedChannels = new Map<string, SharedChannelEntry>();

/**
 * Prefer this over calling `supabase.channel(topic)` directly whenever more than
 * one mounted component may subscribe to the same topic.
 *
 * Supabase reuses channels by topic name and throws if `.on()` runs after
 * `.subscribe()` on an existing channel — that crash previously took down the
 * whole app (NotificationBell, Squad Live Radar, squad chat).
 *
 * `create` runs once for the first subscriber: build the channel, attach `.on()`
 * handlers that call `broadcast()`, then `.subscribe()`. Later subscribers only
 * register their listener. The channel is removed when the last listener releases.
 *
 * @example
 * useEffect(() => {
 *   if (!userId) return;
 *   return acquireSharedRealtimeChannel(
 *     `notifications-${userId}`,
 *     (broadcast) =>
 *       supabase
 *         .channel(`notifications-${userId}`)
 *         .on("postgres_changes", { event: "*", schema: "public", table: "notifications", filter: `recipient_id=eq.${userId}` }, broadcast)
 *         .subscribe(),
 *     () => { void queryClient.invalidateQueries(...); },
 *   );
 * }, [userId, queryClient]);
 */
export function acquireSharedRealtimeChannel(
  topic: string,
  create: (broadcast: () => void) => RealtimeChannel,
  listener: () => void,
): () => void {
  let entry = sharedChannels.get(topic);
  if (!entry) {
    const listeners = new Set<() => void>();
    const channel = create(() => {
      for (const l of listeners) l();
    });
    entry = { channel, refs: 0, listeners };
    sharedChannels.set(topic, entry);
  }

  entry.refs += 1;
  entry.listeners.add(listener);

  return () => {
    const current = sharedChannels.get(topic);
    if (!current) return;
    current.listeners.delete(listener);
    current.refs -= 1;
    if (current.refs > 0) return;
    void supabase.removeChannel(current.channel);
    sharedChannels.delete(topic);
  };
}
